import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Flex, FormControl, FormLabel, Input, Checkbox, Text } from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import PropTypes from 'prop-types';
import ModalLayout from '../common/modal/modalLayout';
import { openModal, Modals, closeModal } from '../../redux/reducers';
import {
  checkUser,
  createUser,
  addUserToOrganization,
  removeUserFromOrganization,
  updateUser,
} from './api';

const MODE = {
  check: 'check',
  alreadyMember: 'alreadyMember',
  notInterpreret: 'notInterpreter',
  add: 'add',
  create: 'create',
  update: 'update',
};

const UserForm = ({ user, mode, changeProp, onRemove, onCheck }) => {
  const { t } = useTranslation();
  const onEnter = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        onCheck();
        e.preventDefault();
      }
    },
    [onCheck]
  );

  return (
    <Box>
      {mode === MODE.update && onRemove && (
        <Button
          leftIcon={<DeleteIcon />}
          bg="brand.60"
          color="white"
          sx={{ float: 'right' }}
          onClick={onRemove}
        >
          {t('user.remove')}
        </Button>
      )}
      <Text>{t('interpreter.form.mandatoryFieldInfo')}</Text>
      <Flex alignItems="flex-end">
        <FormControl isRequired>
          <FormLabel mt={4}>{t('user.email')}</FormLabel>
          <Input
            bg="brand.10"
            borderRadius={0}
            defaultValue={user.username}
            onChange={(e) => changeProp('username', e.target.value)}
            disabled={mode !== MODE.check}
            placeholder={t('user.placeholder')}
            onKeyDown={onEnter}
          />
        </FormControl>
        <Button ml="2" onClick={onCheck} visibility={mode === MODE.check ? 'visible' : 'hidden'}>
          {t('user.check')}
        </Button>
      </Flex>
      {mode === MODE.check ? (
        <Box height="200px" />
      ) : mode === MODE.alreadyMember ? (
        <Box height="200px" mt="4">
          {t('user.alreadyMember', { organization: user.organization.name })}
        </Box>
      ) : mode === MODE.notInterpreret ? (
        <Box height="200px" mt="4">
          {t('user.notInterpreter')}
        </Box>
      ) : (
        <>
          <Flex>
            <FormControl isRequired width="45%" mr="2">
              <FormLabel mt={4}>{t('user.firstName')}</FormLabel>
              <Input
                bg="brand.10"
                borderRadius={0}
                defaultValue={user.firstname}
                onChange={(e) => changeProp('firstname', e.target.value)}
                disabled={mode === MODE.add}
                placeholder={t('user.placeholder')}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel mt={4}>{t('user.lastName')}</FormLabel>
              <Input
                bg="brand.10"
                borderRadius={0}
                defaultValue={user.lastname}
                onChange={(e) => changeProp('lastname', e.target.value)}
                disabled={mode === MODE.add}
                placeholder={t('user.placeholder')}
              />
            </FormControl>
          </Flex>
          <FormControl>
            <FormLabel mt={4}>{t('user.phoneNumber')}</FormLabel>
            <Input
              bg="brand.10"
              borderRadius={0}
              defaultValue={user.phoneNumber}
              onChange={(e) => changeProp('phoneNumber', e.target.value)}
              disabled={mode === MODE.add}
              placeholder={t('user.placeholder')}
            />
          </FormControl>
          <FormControl mr="2" mt="4" display="flex" alignItems="baseline">
            <Checkbox
              defaultIsChecked={user.isAdmin}
              onChange={(e) => changeProp('isAdmin', e.target.checked)}
            />
            <FormLabel flexGrow="1" ml={2}>
              {t('user.isAdmin')}
            </FormLabel>
          </FormControl>
        </>
      )}
    </Box>
  );
};

UserForm.propTypes = {
  user: PropTypes.object.isRequired,
  mode: PropTypes.string.isRequired,
  changeProp: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  onCheck: PropTypes.func,
};

const validateUser = (user) => {
  return !!(user.username && user.firstname && user.lastname);
};

const EditUserModal = ({
  createNew,
  user: initialValue,
  organizationId,
  open,
  onUpdate,
  ...rest
}) => {
  const [mode, setMode] = useState('check'); // check email, add existing, create new, update
  const [user, setUser] = useState({});
  const [isValid, setValid] = useState(false);
  useEffect(() => {
    if (open) {
      if (initialValue) {
        // if modal is reopened with same user, dont clear form fields
        if (initialValue.id != user.id) {
          setUser({ ...initialValue });
          setValid(validateUser(initialValue));
          setMode(MODE.update);
        }
      } else {
        // console.log('user', user);
        if (user.id) {
          setUser({});
          setValid(false);
          setMode(MODE.check);
        }
      }
    }
  }, [open]);

  const checkIfUserExists = useCallback(async () => {
    const response = await checkUser(user.username);
    // console.log('checkIfUserExists', response);
    if (!response.found) {
      user.id = 'createNew';
      setMode(MODE.create);
    } else if (!response.isInterpreter) {
      setUser(response.user);
      setMode(MODE.notInterpreret);
    } else if (!response.organization) {
      setUser(response.user);
      setMode(MODE.add);
    } else {
      response.user.organization = response.organization;
      setUser(response.user);
      setMode(MODE.alreadyMember);
    }
  }, [user]);

  const changeProp = useCallback(
    (prop, value) => {
      user[prop] = value;
      setValid(validateUser(user));
    },
    [user]
  );

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const onClose = useCallback(() => {
    dispatch(closeModal());
  });

  const addCreateOrUpdateUser = useCallback(async () => {
    setSaveError(null);
    setIsSaving(true);
    try {
      let response;
      if (mode === MODE.add) {
        response = await addUserToOrganization(user.id, organizationId, user.isAdmin);
      } else if (mode === MODE.create) {
        response = await createUser(organizationId, user);
        user.id = response.id;
      } else if (mode === MODE.update) {
        response = await updateUser(user.id, organizationId, user);
      }
      onClose();
      onUpdate && onUpdate(response);
    } catch (error) {
      error.user = user;
      setSaveError(error);
    } finally {
      setIsSaving(false);
    }
  }, [user, mode, organizationId]);

  useEffect(() => {
    if (saveError != null) {
      const { user } = saveError;
      dispatch(
        // Closing error modal should open edit modal again so that user can try saving again without having to edit sessions data again
        openModal(
          {
            headerText: t('modal.error'),
            bodyText: `${t('interpreter.form.updateError')}: ${saveError?.message}`,
            onRightButtonClick: () => {
              dispatch(openModal({ user }, Modals.EDIT_USER));
            },
            onClose: () => {
              dispatch(openModal({ user }, Modals.EDIT_USER));
            },
          },
          Modals.ERROR
        )
      );
    }
  }, [saveError]);

  const canRemove = mode === MODE.update;
  const onRemove = useCallback(async () => {
    setSaveError(null);
    try {
      await removeUserFromOrganization(user.id, organizationId);
      onClose();
      user.removed = true;
      onUpdate && onUpdate(user);
    } catch (error) {
      error.user = user;
      setSaveError(error);
    }
  }, [user, organizationId]);

  return (
    <ModalLayout
      open={open}
      headerText={t(
        createNew
          ? mode === MODE.create
            ? 'user.createTitle'
            : 'user.addTitle'
          : 'user.updateTitle'
      )}
      {...rest}
      leftButtonProps={{ bg: null }}
      rightButtonProps={{ color: 'white' }}
      leftButtonText={t('modal.cancel')}
      rightButtonText={mode === MODE.update ? t('modal.save') : t('modal.add')}
      disableRightButton={isSaving || !isValid}
      disableLeftButton={isSaving}
      onLeftButtonClick={onClose}
      onRightButtonClick={addCreateOrUpdateUser}
      showProgress={isSaving}
      onClose={onClose}
      size="lg"
    >
      <UserForm
        key={user.id}
        user={user}
        mode={mode}
        changeProp={changeProp}
        onRemove={canRemove ? onRemove : null}
        onCheck={checkIfUserExists}
      />
    </ModalLayout>
  );
};

EditUserModal.propTypes = {
  createNew: PropTypes.bool,
  user: PropTypes.object,
  organizationId: PropTypes.string,
  open: PropTypes.bool,
  onUpdate: PropTypes.func,
};

EditUserModal.defaultProps = {
  open: false,
};

export default EditUserModal;
